import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { authenticationReferenceMethodOptions, azureResourceSelectMethodOptions } from '../consts/metadata-consts';
import { azureKeyVaultAuthRefTypes, azureKeyVaultAuthTypes, CredentialTypeOptions } from 'shell-models-lib';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { GlobalVariables } from 'app/services/globals';
import { ISelectOption } from 'app/interfaces/ISelectOption';
import { mergeUamiCredentialOption, mergeUserAuthCredentialOption } from '../utls/authenticationUtils';
import { keyVaultDomain } from 'app/shared/utils/connector-metadata-static-consts';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';

const parameterizableUrl: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'baseUrl',
        label: stringResources.ModelName_BaseUrl,
        required: true,
        placeholder: `e.g., https://<azureKeyVaultName>.${keyVaultDomain}`,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true
    }
};

const defaultKVAuthenticationOptions: Array<ISelectOption<string>> = [
    {
        label: stringResources.AuthenticationType_ManagedServiceIdentity,
        value: azureKeyVaultAuthTypes.MSIAuthentication
    }
];

const msiControl: ControlMetadata = {
    type: 'management-identity-info',
    resourceType: stringResources.AzureKeyVault_DisplayText,
    link: 'https://docs.microsoft.com/azure/data-factory/store-credentials-in-key-vault'
};

const azureKeyVaultSetting: ControlMetadata = {
    type: 'radio-switch',
    nested: 1,
    label: stringResources.AzureKeyVault_AccountName_SelectionMethod,
    description: stringResources.AzureKeyVault_AccountName_SelectionMethod_Description,
    options: azureResourceSelectMethodOptions,
    isLabelVertical: true,
    defaultValue: '{baseUrl != null || parameterized ? "manual" : "azure"}',
    cases: [
        {
            caseValue: 'azure',
            children: [
                {
                    type: 'azure-key-vault-select',
                    bind: {
                        baseUrl: 'baseUrl',
                        resourceID: '_akvResourceID'
                    },
                    validators: {
                        baseUrl: [{ type: 'required', errorMessage: '' }]
                    }
                }
            ]
        },
        {
            caseValue: 'manual',
            children: [parameterizableUrl]
        }
    ]
};

// provides user with inline vs credential radio button to add authentication parameters for SAMI and SP Authentication methods.
function azureKeyVaultAuthReferenceSetting(credentialTypeFilter: CredentialTypeOptions): ControlMetadata {
    return {
        type: 'radio-switch',
        bind: 'authRefMethod',
        nested: 1,
        label: stringResources.Authentication_Reference_Method_Label,
        description: stringResources.Authentication_Reference_Method_Description,
        options: authenticationReferenceMethodOptions,
        isLabelVertical: true,
        defaultValue: azureKeyVaultAuthRefTypes.InlineRefTypeAuthentication,
        cases: [
            {
                caseValue: azureKeyVaultAuthRefTypes.InlineRefTypeAuthentication,
                children: [
                    // system Assigned Managed Identity
                    msiControl
                ]
            },
            {
                caseValue: azureKeyVaultAuthRefTypes.CredentialRefTypeAuthentication,
                children: [
                    {
                        type: 'credential-select',
                        bind: 'credential',
                        filterCredentialType: credentialTypeFilter,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }]
                    }
                ]
            }
        ]
    };
}

export const azureKeyVaultMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.azureKeyVault.linkedServiceType,
        displayName: stringResources.AzureKeyVault_DisplayText,
        icon: {
            className: 'svg-i-100x100-azure_key_vault'
        },
        supportedInTrident: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'AzureKeyVault' as ResourceKind
        },
        noIR: true,
        isFirstClass: true,
        copySinkTypes: [],
        supportPE: true,
        peGroupIDs: ['vault'],
        peWellknowErrorMessages: [/Client address is not authorized and caller is not a trusted service/i],
        resourceTypes: ['Microsoft.KeyVault/vaults'],
        copySourceTypes: [],
        category: ['Azure'],
        controls: [
            azureKeyVaultSetting,
            {
                type: 'select-switch',
                bind: 'authenticationMethod',
                label: stringResources.Authentication_Method_Label,
                options: mergeUserAuthCredentialOption(
                    mergeUamiCredentialOption(defaultKVAuthenticationOptions),
                    azureKeyVaultAuthTypes.UserAuth
                ),
                isLabelVertical: true,
                defaultValue: azureKeyVaultAuthTypes.MSIAuthentication,
                cases: [
                    {
                        caseValue: azureKeyVaultAuthTypes.MSIAuthentication,
                        children: GlobalVariables.isSamiSupportEnabled
                            ? [azureKeyVaultAuthReferenceSetting(CredentialTypeOptions.ManagedIdentity)]
                            : [msiControl]
                    },
                    {
                        caseValue: azureKeyVaultAuthTypes.UserAssignedManagedIdentity,
                        children: [
                            {
                                type: 'credential-select',
                                bind: 'credential',
                                filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }]
                            }
                        ]
                    },
                    {
                        caseValue: azureKeyVaultAuthTypes.UserAuth,
                        children: [
                            {
                                type: 'credential-select',
                                bind: 'credential',
                                filterCredentialType: CredentialTypeOptions.UserAuth,
                                hide: !GlobalVariables.enableConnectorUserAuthCred,
                                required: true,
                                validators: [{ type: 'required', errorMessage: '' }]
                            }
                        ]
                    }
                ]
            }
        ],
        supportFirstClassParameterExperience: true
    };

    return metadata;
};
